import React, { useEffect, useState } from "react";
import Select from "react-select";
import { validarAtendimentoDisponivel } from "./Helpers/AgendaValidation";
import { getDataUltimaConsultaPorIdPaciente } from "./ConsultaService";
import { getTipoConsultaPorIdPacienteEData } from "./AgendaService";
import { getConfiguracoesAtendimentoMedicoPorUsuario } from "./ConfiguracaoHorarioMedicoService";
import ConfiguracaoPacienteModal from "./ConfiguracaoPacienteModal";

const TipoConsulta = {
    PARTICULAR: "Particular",
    CONVENIO: "Convênio",
    RETORNO: "Retorno",
};

const normalizarDataParaApi = (data) => {
    if (!data) {
        return "";
    }

    let texto = String(data).trim();
    const textoOriginal = texto;
    if (texto.startsWith("\"") && texto.endsWith("\"") && texto.length > 1) {
        texto = texto.slice(1, -1).trim();
    }
    if (texto.includes("T")) {
        texto = texto.split("T")[0];
    }
    if (texto.includes(" ")) {
        texto = texto.split(" ")[0];
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
        return texto;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) {
        const [dia, mes, ano] = texto.split("/");
        return `${ano}-${mes}-${dia}`;
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(texto)) {
        const [dia, mes, ano] = texto.split("-");
        return `${ano}-${mes}-${dia}`;
    }

    const textoSemAcento = textoOriginal
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    const matchPt = textoSemAcento.match(
        /(\d{1,2})\s+de\s+([a-z]+)\s+de\s+(\d{4})/,
    );
    if (matchPt) {
        const meses = {
            janeiro: "01",
            fevereiro: "02",
            marco: "03",
            abril: "04",
            maio: "05",
            junho: "06",
            julho: "07",
            agosto: "08",
            setembro: "09",
            outubro: "10",
            novembro: "11",
            dezembro: "12",
        };
        const dia = matchPt[1].padStart(2, "0");
        const mes = meses[matchPt[2]];
        if (mes) {
            return `${matchPt[3]}-${mes}-${dia}`;
        }
    }

    const matchIso = textoOriginal.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (matchIso) {
        return `${matchIso[1]}-${matchIso[2]}-${matchIso[3]}`;
    }
    const matchBarra = textoOriginal.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (matchBarra) {
        return `${matchBarra[3]}-${matchBarra[2]}-${matchBarra[1]}`;
    }
    const matchHifen = textoOriginal.match(/(\d{2})-(\d{2})-(\d{4})/);
    if (matchHifen) {
        return `${matchHifen[3]}-${matchHifen[2]}-${matchHifen[1]}`;
    }

    return "";
};

const calcularDiferencaEmDias = (dataIso) => {
    if (!dataIso) {
        return null;
    }

    const partes = dataIso.split("-");
    if (partes.length !== 3) {
        return null;
    }

    const [ano, mes, dia] = partes.map((parte) => Number(parte));
    if (!ano || !mes || !dia) {
        return null;
    }

    const dataBase = new Date(ano, mes - 1, dia);
    const hoje = new Date();
    const hojeBase = new Date(
        hoje.getFullYear(),
        hoje.getMonth(),
        hoje.getDate(),
    );
    const diffMs = hojeBase - dataBase;
    if (Number.isNaN(diffMs)) {
        return null;
    }

    return Math.round(diffMs / 86400000);
};

const formatarDiferencaDias = (dias) => {
    if (dias === null) {
        return "";
    }
    if (dias === 0) {
        return " - hoje";
    }

    const abs = Math.abs(dias);
    return dias > 0 ? ` - ha ${abs} dias` : ` - em ${abs} dias`;
};

const DiasDaSemana = {
    DOMINGO: "Domingo",
    SEGUNDA_FEIRA: "Segunda-feira",
    TERCA_FEIRA: "Terca-feira",
    QUARTA_FEIRA: "Quarta-feira",
    QUINTA_FEIRA: "Quinta-feira",
    SEXTA_FEIRA: "Sexta-feira",
    SABADO: "Sabado",
};

const formatarHoraAtendimento = (hora) => {
    if (Array.isArray(hora)) {
        const [horaValor, minutoValor = 0] = hora;
        return `${String(horaValor).padStart(2, "0")}:${String(
            minutoValor,
        ).padStart(2, "0")}`;
    }

    return String(hora ?? "").substring(0, 5);
};

const formatarConfiguracaoHorarioMedico = (configuracao) => {
    const dia = DiasDaSemana[configuracao.diasDaSemana] || configuracao.diasDaSemana;
    const horaInicio = formatarHoraAtendimento(configuracao.horaInicio);
    const horaFim = formatarHoraAtendimento(configuracao.horaFim);

    return `${dia}: ${horaInicio} as ${horaFim}`;
};

// const StatusAgenda = {
//     CONFIRMADA: "Confirmada",
//     FALTA: "Falta",
//     AGUARDANDO: "Aguardando"
// };

const NovoEventoModal = ({
    dataSelecionada,
    token,
    isModalOpen,
    onCloseModal,
    pacientes,
    pacienteParaEditar,
    funcionarios,
    horaSelecionada = "",
    funcionarioPreSelecionado = null,
}) => {
    const [pacienteSelecionado, setPacienteSelecionado] = useState(0);
    const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
    const [temPacienteParaEditar, setTemPacienteParaEditar] = useState(
        pacienteParaEditar !== null && pacienteParaEditar,
    );
    const [dataUltimaConsulta, setDataUltimaConsulta] = useState("");
    const [carregandoUltimaConsulta, setCarregandoUltimaConsulta] =
        useState(false);
    const [tipoUltimaConsulta, setTipoUltimaConsulta] = useState("");
    const [carregandoTipoUltimaConsulta, setCarregandoTipoUltimaConsulta] =
        useState(false);
    const [horariosMedico, setHorariosMedico] = useState([]);
    const [carregandoHorariosMedico, setCarregandoHorariosMedico] =
        useState(false);
    const [nomePacienteParaCadastro, setNomePacienteParaCadastro] =
        useState("");
    const [modalCadastroPacienteOpen, setModalCadastroPacienteOpen] =
        useState(false);
    const [consultasPacienteCadastro, setConsultasPacienteCadastro] =
        useState([]);
    const [isButtonStaticPacienteCadastro, setIsButtonStaticPacienteCadastro] =
        useState(true);
    // const [funcionarios, setFuncionarios] = useState([]);
    const [observacao, setObservacao] = useState("");
    const [formData, setFormData] = useState({
        paciente: { pacienteSelecionado },
        dataDaConsulta: '',
        horaDaConsulta: "",
        funcionario: { funcionarioSelecionado },
        statusAgenda: "AGUARDANDO",
        observacao: "",
        tipoConsulta: "",
    });

    const handleChange = (event) => {
        const { name, value } = event.target;

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const funcionarioId =
            formData.funcionario?.id ??
            formData.funcionario ??
            funcionarioSelecionado?.id;
        const funcionarioDaConsulta =
            funcionarios?.find((func) => func.id === funcionarioId) ||
            funcionarioSelecionado;

        const validacaoAtendimento = validarAtendimentoDisponivel({
            dataDaConsulta: formData.dataDaConsulta,
            horaDaConsulta: formData.horaDaConsulta,
            configuracoesHorario: horariosMedico,
            diasDeAtendimento: funcionarioDaConsulta?.diasDeAtendimento,
        });

        if (!validacaoAtendimento.permitido) {
            alert(validacaoAtendimento.motivo);
            return;
        }

        fetch(`${process.env.REACT_APP_URL_API}/api/agenda`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: token,
            },
            body: JSON.stringify(formData),
        })
            .then((response) => {
                if (response.ok) {
                    window.location.reload();
                } else {
                    alert("Erro ao enviar dados.");
                }
            })
            .catch((error) => {
                console.error("Erro na requisição:", error);
            });
    };

    const changePacienteSelecionado = (e) => {
        setPacienteSelecionado(e);

        setFormData({
            ...formData,
            paciente: e.id,
        });
    };

    const abrirModalCadastroPaciente = (nomePaciente) => {
        setNomePacienteParaCadastro(nomePaciente.trim());
        setConsultasPacienteCadastro([]);
        setIsButtonStaticPacienteCadastro(true);
        setModalCadastroPacienteOpen(true);
    };

    const fecharModalCadastroPaciente = () => {
        setModalCadastroPacienteOpen(false);
    };

    const handleInputPacienteChange = (value, actionMeta) => {
        if (actionMeta.action === "input-change") {
            setNomePacienteParaCadastro(value);
        }

        return value;
    };

    const renderNoOptionsPaciente = ({ inputValue }) => {
        const nomeDigitado = inputValue.trim() || nomePacienteParaCadastro.trim();
        const textoAcao = nomeDigitado
            ? `Cadastrar paciente "${nomeDigitado}"`
            : "Cadastrar novo paciente";

        return (
            <button
                type="button"
                className="w-full px-3 py-2 text-center text-sm font-medium text-cyan-700 hover:bg-cyan-50"
                onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    abrirModalCadastroPaciente(nomeDigitado);
                }}
            >
                {textoAcao}
            </button>
        );
    };

    useEffect(() => {
        const pacienteId =
            pacienteSelecionado?.id ?? pacienteParaEditar?.id ?? null;

        if (!pacienteId) {
            setDataUltimaConsulta("");
            setTipoUltimaConsulta("");
            return;
        }

        let ativo = true;

        const carregarDataUltimaConsulta = async () => {
            setCarregandoUltimaConsulta(true);
            try {
                const data = await getDataUltimaConsultaPorIdPaciente(
                    pacienteId,
                    token,
                );
                if (ativo) {
                    const texto = String(data ?? "").trim();
                    setDataUltimaConsulta(
                        texto && texto.toLowerCase() !== "null" ? texto : "",
                    );
                    setTipoUltimaConsulta("");
                }
            } catch (error) {
                console.error(
                    "Erro ao buscar data da ultima consulta:",
                    error,
                );
                if (ativo) {
                    setDataUltimaConsulta("");
                    setTipoUltimaConsulta("");
                }
            } finally {
                if (ativo) {
                    setCarregandoUltimaConsulta(false);
                }
            }
        };

        carregarDataUltimaConsulta();

        return () => {
            ativo = false;
        };
    }, [pacienteSelecionado, pacienteParaEditar, token]);

    useEffect(() => {
        const pacienteId =
            pacienteSelecionado?.id ?? pacienteParaEditar?.id ?? null;
        const dataParaApi = normalizarDataParaApi(dataUltimaConsulta);

        if (!pacienteId || !dataParaApi) {
            setTipoUltimaConsulta("");
            return;
        }

        let ativo = true;

        const carregarTipoUltimaConsulta = async () => {
            setCarregandoTipoUltimaConsulta(true);
            try {
                const tipo = await getTipoConsultaPorIdPacienteEData(
                    token,
                    pacienteId,
                    dataParaApi,
                );
                if (ativo) {
                    const texto = String(tipo ?? "").trim();
                    setTipoUltimaConsulta(
                        texto && texto.toLowerCase() !== "null" ? texto : "",
                    );
                }
            } catch (error) {
                console.error(
                    "Erro ao buscar tipo da ultima consulta:",
                    error,
                );
                if (ativo) {
                    setTipoUltimaConsulta("");
                }
            } finally {
                if (ativo) {
                    setCarregandoTipoUltimaConsulta(false);
                }
            }
        };

        carregarTipoUltimaConsulta();

        return () => {
            ativo = false;
        };
    }, [dataUltimaConsulta, pacienteSelecionado, pacienteParaEditar, token]);

    useEffect(() => {
        if (dataSelecionada) {
            const funcionarioInicial =
                funcionarioPreSelecionado?.id ? funcionarioPreSelecionado : null;

            if (funcionarioInicial) {
                setFuncionarioSelecionado(funcionarioInicial);
            }

            setFormData({
                paciente: { pacienteSelecionado },
                dataDaConsulta: dataSelecionada,
                horaDaConsulta: horaSelecionada || "",
                funcionario: funcionarioInicial?.id ?? { funcionarioSelecionado },
                statusAgenda: "AGUARDANDO",
                observacao: "",
                tipoConsulta: "",
            });
        }
    }, [dataSelecionada, horaSelecionada, funcionarioPreSelecionado]);

    const changeFuncionarioSelecionado = (e) => {
        setFuncionarioSelecionado(e);

        setFormData({
            ...formData,
            funcionario: e.id,
        });
    };

    useEffect(() => {
        const medicoId = funcionarioSelecionado?.id ?? null;

        if (!medicoId) {
            setHorariosMedico([]);
            return;
        }

        let ativo = true;

        const carregarHorariosMedico = async () => {
            setCarregandoHorariosMedico(true);
            try {
                const horarios =
                    await getConfiguracoesAtendimentoMedicoPorUsuario(
                        token,
                        medicoId,
                    );

                if (ativo) {
                    setHorariosMedico(Array.isArray(horarios) ? horarios : []);
                }
            } catch (error) {
                console.error(
                    "Erro ao buscar horarios de atendimento do medico:",
                    error,
                );
                if (ativo) {
                    setHorariosMedico([]);
                }
            } finally {
                if (ativo) {
                    setCarregandoHorariosMedico(false);
                }
            }
        };

        carregarHorariosMedico();

        return () => {
            ativo = false;
        };
    }, [funcionarioSelecionado, token]);

    const changeTipoConsultaSelecionada = (e) => {
        // setFuncionarioSelecionado(e);

        setFormData({
            ...formData,
            tipoConsulta: e.target.value,
        });
    };

    const changeObservacao = (e) => {
        // setFuncionarioSelecionado(e);

        setFormData({
            ...formData,
            observacao: e.target.value,
        });
    };

    const dataUltimaConsultaParaApi =
        normalizarDataParaApi(dataUltimaConsulta);
    const diasDesdeUltimaConsulta = calcularDiferencaEmDias(
        dataUltimaConsultaParaApi,
    );

    return (
        <>
            {isModalOpen && (
                <div
                    id="top-right-modal"
                    data-modal-placement="top-right"
                    tabIndex="-1"
                    className="bg-black bg-opacity-50 fixed top-0 left-0 right-0 z-50 w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] min-h-full justify-center items-start flex"
                >
                    <div className="relative w-full max-w-md max-h-full">
                        {/* <!-- Modal content --> */}
                        <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
                            {/* <!-- Modal header --> */}
                            <div className="flex items-start justify-between p-4 border-b rounded-t dark:border-gray-600">
                                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                    Adicionar na agenda
                                </h3>
                                <button
                                    onClick={onCloseModal}
                                    type="button"
                                    className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                                    data-modal-hide="novo-evento-modal"
                                >
                                    <svg
                                        className="w-3 h-3"
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 14 14"
                                    >
                                        <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                                        />
                                    </svg>
                                    <span className="sr-only">Close modal</span>
                                </button>
                            </div>
                            <div className="p-6 space-y-6">
                                <form
                                    onSubmit={handleSubmit}
                                    id="formAgendamento"
                                >
                                    <div className="mb-6">
                                        {/* {pacienteSelecionado ? JSON.stringify(pacienteSelecionado) : '---'} */}
                                        <Select
                                            isDisabled={temPacienteParaEditar}
                                            onChange={changePacienteSelecionado}
                                            onInputChange={handleInputPacienteChange}
                                            value={
                                                pacienteSelecionado ||
                                                pacienteParaEditar
                                            }
                                            maxMenuHeight={150}
                                            options={pacientes}
                                            noOptionsMessage={renderNoOptionsPaciente}
                                            placeholder="Selecione o paciente"
                                            required
                                        />
                                        {(pacienteSelecionado ||
                                            pacienteParaEditar) && (
                                            <p className="mt-2 text-xs text-gray-500">
                                                Ultima consulta:{" "}
                                                {carregandoUltimaConsulta ? (
                                                    "carregando..."
                                                ) : dataUltimaConsulta ? (
                                                    <>
                                                        {dataUltimaConsulta}
                                                        {carregandoTipoUltimaConsulta
                                                            ? " (carregando tipo...)"
                                                            : tipoUltimaConsulta
                                                              ? ` (${tipoUltimaConsulta})`
                                                              : " (tipo nao informado)"}
                                                        {formatarDiferencaDias(
                                                            diasDesdeUltimaConsulta,
                                                        )}
                                                    </>
                                                ) : (
                                                    "nao informada"
                                                )}
                                            </p>
                                        )}
                                        {/* <h6 className="mb-3">Dados do paciente</h6> */}
                                    </div>
                                    <div className="grid md:grid-cols-2 md:gap-6">
                                        <div className="relative z-0 w-full mb-6 group">
                                            <input
                                                onChange={handleChange}
                                                value={formData.dataDaConsulta}
                                                type="date"
                                                name="dataDaConsulta"
                                                id="dataDaConsulta"
                                                className="block px-2.5 pb-1.5 pt-3 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                                                placeholder="DD/MM/AAAA"
                                                required
                                            />
                                            <label
                                                for="dataDaConsulta"
                                                className="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-3 scale-75 top-1 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-1 peer-focus:scale-75 peer-focus:-translate-y-3 left-1"
                                            >
                                                Data da consulta
                                            </label>
                                        </div>
                                        <div className="relative z-0 w-full mb-6 group">
                                            <input
                                                onChange={handleChange}
                                                value={formData.horaDaConsulta}
                                                type="time"
                                                name="horaDaConsulta"
                                                id="horaDaConsulta"
                                                className="block px-2.5 pb-1.5 pt-3 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                                                placeholder=" "
                                                required
                                            />
                                            <label
                                                for="horaDaConsulta"
                                                className="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-3 scale-75 top-1 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-1 peer-focus:scale-75 peer-focus:-translate-y-3 left-1"
                                            >
                                                Hora da consulta
                                            </label>
                                        </div>
                                    </div>
                                    <div className="mb-6">
                                        {/* {pacienteSelecionado ? JSON.stringify(pacienteSelecionado) : '---'} */}
                                        <Select
                                            onChange={
                                                changeFuncionarioSelecionado
                                            }
                                            value={funcionarioSelecionado}
                                            maxMenuHeight={150}
                                            options={funcionarios}
                                            placeholder="Selecione o médico"
                                            required
                                        />
                                        {funcionarioSelecionado && (
                                            <p className="mt-2 text-xs text-gray-500">
                                                Atendimento:{" "}
                                                {carregandoHorariosMedico
                                                    ? "carregando..."
                                                    : horariosMedico.length
                                                      ? horariosMedico
                                                            .map(
                                                                formatarConfiguracaoHorarioMedico,
                                                            )
                                                            .join("; ")
                                                      : "nao informado"}
                                            </p>
                                        )}
                                        {/* <h6 className="mb-3">Dados do paciente</h6> */}
                                    </div>
                                    <div className="mb-6">
                                        <label
                                            for="tipoConsulta"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            Tipo da consulta:
                                        </label>

                                        <select
                                            disabled={
                                                funcionarioSelecionado === null
                                            }
                                            id="tipoConsulta"
                                            name="tipoConsulta"
                                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            required
                                            onChange={
                                                changeTipoConsultaSelecionada
                                            }
                                            value={formData.tipoConsulta}
                                        >
                                            {/* <option value="">Tipo de consulta</option> */}
                                            {/* <option value="particular">Particular</option>
                                                    <option value="convenio">Convênio</option>
                                                    <option value="retorno">Retorno</option> */}
                                            <option value="">
                                                Tipo da consulta
                                            </option>
                                            {Object.keys(TipoConsulta).map(
                                                (chave, index) => (
                                                    <option
                                                        key={index}
                                                        value={chave}
                                                    >
                                                        {TipoConsulta[chave]}
                                                    </option>
                                                ),
                                            )}
                                        </select>
                                    </div>
                                    {/* <div className="mb-6">
                                        <label
                                            for="statusAgenda"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            Status da consulta:
                                        </label>

                                        <select
                                            disabled={
                                                funcionarioSelecionado === null
                                            }
                                            id="statusAgenda"
                                            name="statusAgenda"
                                            className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            required
                                            // onChange={
                                            //     changeTipoConsultaSelecionada
                                            // }
                                            value={formData.statusAgenda}
                                        >
                                            {Object.keys(StatusAgenda).map(
                                                (chave, index) => (
                                                    <option
                                                        key={index}
                                                        value={chave}
                                                    >
                                                        {StatusAgenda[chave]}
                                                    </option>
                                                ),
                                            )}
                                        </select>
                                    </div> */}
                                    <div className="mb-6">
                                        <label
                                            for="observacoes"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            Observações
                                        </label>
                                        <textarea
                                            onChange={changeObservacao}
                                            value={formData.observacao}
                                            name="observacoes"
                                            id="observacoes"
                                            rows="4"
                                            className="no-resize mb-5 block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="Digite uma observação..."
                                        ></textarea>
                                    </div>
                                </form>
                            </div>
                            <div className="flex items-center p-6 space-x-2 border-t border-gray-200 rounded-b dark:border-gray-600">
                                <button
                                    onClick={onCloseModal}
                                    type="button"
                                    className="text-white bg-gradient-to-br from-pink-500 to-orange-400 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-pink-200 dark:focus:ring-pink-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                    data-modal-hide="novo-evento-modal"
                                >
                                    Fechar
                                </button>
                                {/* {isEditar && ( */}
                                <button
                                    type="submit"
                                    form="formAgendamento"
                                    className="text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-cyan-300 dark:focus:ring-cyan-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                >
                                    Salvar
                                </button>
                                {/* // )} */}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ConfiguracaoPacienteModal
                authResponseDTO={null}
                consultasPorIdPaciente={consultasPacienteCadastro}
                setConsultasPorIdPaciente={setConsultasPacienteCadastro}
                isButtonStatic={isButtonStaticPacienteCadastro}
                setIsButtonStatic={setIsButtonStaticPacienteCadastro}
                token={token}
                onModalClose={fecharModalCadastroPaciente}
                modalOpen={modalCadastroPacienteOpen}
                titulo="Novo cadastro"
                isEditar={false}
                paciente={{ nome: nomePacienteParaCadastro }}
                exibeHistorico={false}
            />
        </>
    );
};

export default NovoEventoModal;
